/**
 * Central HTTP client. Talks to the Spring Boot backend with JWT in Authorization header.
 */
import { API_CONFIG } from '../config/api.config';
import { STORAGE_KEYS } from '../config/app.config';
import { getErrorMessage } from '../utils/getErrorMessage';

let toastRef = null;

export function setApiToastRef(ref) {
  toastRef = ref;
}

function buildUrl(path, params = {}) {
  const url = new URL(path.startsWith('http') ? path : `${API_CONFIG.BASE_URL}${path}`);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  });
  return url.toString();
}

function getAuthHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.AUTH);
    if (raw) {
      const auth = JSON.parse(raw);
      if (auth?.token) headers['Authorization'] = `Bearer ${auth.token}`;
    }
  } catch {
    // ignore
  }
  return headers;
}

async function fetchWithTimeout(url, options = {}, timeoutMs = API_CONFIG.TIMEOUT_MS) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        ...getAuthHeaders(),
        ...options.headers,
      },
    });
    clearTimeout(id);
    return res;
  } catch (e) {
    clearTimeout(id);
    throw e;
  }
}

async function handleResponse(res, options = {}) {
  const showToastOnError = options.showToastOnError !== false;
  if (!res.ok) {
    let body;
    try {
      body = await res.json();
    } catch {
      body = {};
    }
    const err = new Error(body?.message || res.statusText || 'Request failed');
    err.response = { status: res.status, data: body };
    if (showToastOnError && toastRef?.error) {
      const msg = getErrorMessage(err);
      toastRef.error(typeof msg === 'string' && msg.startsWith('errors.') ? msg : msg);
    }
    throw err;
  }
  const contentType = res.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return res.json();
  }
  return res.text();
}

/**
 * GET request.
 * @param {string} path - Endpoint path (from API_CONFIG.ENDPOINTS)
 * @param {object} params - Query params
 * @param {{ showToastOnError?: boolean }} options
 */
export async function apiGet(path, params = {}, options = {}) {
  const url = typeof path === 'function' ? path() : buildUrl(path, params);
  const res = await fetchWithTimeout(url, { method: 'GET' }, options.timeout ?? API_CONFIG.TIMEOUT_MS);
  return handleResponse(res, options);
}

/**
 * POST request.
 */
export async function apiPost(path, body = {}, options = {}) {
  const url = typeof path === 'function' ? path() : buildUrl(path);
  const res = await fetchWithTimeout(
    url,
    { method: 'POST', body: JSON.stringify(body) },
    options.timeout ?? API_CONFIG.TIMEOUT_MS
  );
  return handleResponse(res, options);
}

/**
 * PUT request.
 */
export async function apiPut(path, body = {}, options = {}) {
  const url = typeof path === 'function' ? path() : buildUrl(path);
  const res = await fetchWithTimeout(
    url,
    { method: 'PUT', body: JSON.stringify(body) },
    options.timeout ?? API_CONFIG.TIMEOUT_MS
  );
  return handleResponse(res, options);
}

/**
 * PATCH request.
 */
export async function apiPatch(path, body = {}, options = {}) {
  const url = typeof path === 'function' ? path() : buildUrl(path);
  const res = await fetchWithTimeout(
    url,
    { method: 'PATCH', body: JSON.stringify(body) },
    options.timeout ?? API_CONFIG.TIMEOUT_MS
  );
  return handleResponse(res, options);
}

/**
 * DELETE request. options.params = query params (e.g. { force: true } for WooCommerce).
 */
export async function apiDelete(path, options = {}) {
  const pathStr = typeof path === 'function' ? path() : path;
  const url = buildUrl(pathStr, options.params || {});
  const res = await fetchWithTimeout(url, { method: 'DELETE' }, options.timeout ?? API_CONFIG.TIMEOUT_MS);
  return handleResponse(res, options);
}

export default {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  patch: apiPatch,
  delete: apiDelete,
  setToastRef: setApiToastRef,
};
